import React, { useState } from "react"
import { Box, TextField, Grid, useTheme, useMediaQuery } from "@material-ui/core"
import { DeleteOutline as DeleteOutlineIcon } from "@material-ui/icons"
import { MentionTextInput, OutlinedSelect, ExpansionPanel, UploadingList, UploadingImagesList } from ".."
import { TASK_RESPONSE_TYPE } from "../../data"
import { RemoveIconButton } from "../Buttons"
import { CreatorAtAndUploaders } from "../Creators"
import { useMountEffect } from "../../utils"

const responseTypeLabels = {
  [TASK_RESPONSE_TYPE.NONE]: "Checkbox",
  [TASK_RESPONSE_TYPE.NUMBER]: "Number entry",
  [TASK_RESPONSE_TYPE.NUMBER_RATING_5]: "Number rating (1 to 5)",
  [TASK_RESPONSE_TYPE.NUMBER_RATING_10]: "Number rating (1 to 10)",
  [TASK_RESPONSE_TYPE.TEXT]: "Text entry",
  [TASK_RESPONSE_TYPE.PHOTOS]: "Photo submission",
  [TASK_RESPONSE_TYPE.YES_NO_NA]: "Yes, No, N/A buttons",
}

const Fields = ({ onUpdate, index, initialState: { name, description, responseType }, disabled }) => {
  const theme = useTheme()
  const mdDown = useMediaQuery(theme.breakpoints.down("md"))
  const [state, setState] = useState({ name: "", description: "", responseType: "" })

  useMountEffect(() => {
    setState(() => ({ name, description, responseType }))
  })

  const handleUpdate = (propName, value) => {
    onUpdate(index, propName, value)
    setState((prev) => ({ ...prev, [propName]: value }))
  }

  return (
    <>
      <Box mb={2}>
        <Grid container direction="row" spacing={2}>
          <Grid item xs={12} sm={6}>
            <TextField
              required
              variant="outlined"
              label="Step name"
              value={state.name}
              autoFocus={!name && !mdDown}
              onChange={(e) => handleUpdate("name", e.target.value)}
              fullWidth
              disabled={disabled}
              inputProps={{ "data-cy": "TextField-step-name" }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <OutlinedSelect
              label="Type of response"
              id={`responseType${index}`}
              value={state.responseType}
              onChange={(e) => handleUpdate("responseType", e.target.value)}
              disabled={disabled}
              selectInputProps={{ "data-cy": "OutlinedSelect-step-responseType" }}
            >
              <option value={TASK_RESPONSE_TYPE.NONE}>{responseTypeLabels[TASK_RESPONSE_TYPE.NONE]}</option>
              <option value={TASK_RESPONSE_TYPE.PHOTOS}>{responseTypeLabels[TASK_RESPONSE_TYPE.PHOTOS]}</option>
              <option value={TASK_RESPONSE_TYPE.TEXT}>{responseTypeLabels[TASK_RESPONSE_TYPE.TEXT]}</option>
              <option value={TASK_RESPONSE_TYPE.NUMBER}>{responseTypeLabels[TASK_RESPONSE_TYPE.NUMBER]}</option>
              <option value={TASK_RESPONSE_TYPE.NUMBER_RATING_5}>
                {responseTypeLabels[TASK_RESPONSE_TYPE.NUMBER_RATING_5]}
              </option>
              <option value={TASK_RESPONSE_TYPE.NUMBER_RATING_10}>
                {responseTypeLabels[TASK_RESPONSE_TYPE.NUMBER_RATING_10]}
              </option>
              <option value={TASK_RESPONSE_TYPE.YES_NO_NA}>{responseTypeLabels[TASK_RESPONSE_TYPE.YES_NO_NA]}</option>
            </OutlinedSelect>
          </Grid>
        </Grid>
      </Box>
      <MentionTextInput
        variant="outlined"
        label="Step description"
        minHeight={136}
        value={state.description}
        onChange={(e) => handleUpdate("description", e.target.value)}
        fullWidth
        multiline
        disabled={disabled}
        data-cy="TextField-step-description"
      />
    </>
  )
}

const ProcessStepCreator = ({
  stepIndex: index,
  onUpdate,
  onRemove,
  onUploadingStateChange,
  step: { name, description, responseType, expanded, uploads },
  sortable = true,
  stepDisabled: disabled,
}) => {
  const [uploading, setUploading] = useState([])
  const [isUploading, setIsUploading] = useState(false)

  // const handleAtClick = () => {
  //   onUpdate(index, "description", description ? `${description.trimEnd()} @` : "@")
  // }

  const handleBeforeUpload = (items) => {
    if (onUploadingStateChange) onUploadingStateChange(true)
    setIsUploading(true)
    setUploading(() => items)
  }
  const handleUploadProgress = (items) => {
    setUploading(() => items)
  }
  const handleItemUploaded = (item) => {
    onUpdate(index, "uploads", (state) => {
      state[index].uploads = [...state[index].uploads, item]
      return [...state]
    })
  }
  const handleRemoveUpload = (id) => {
    onUpdate(index, "uploads", [...uploads.filter((upload) => upload.id !== id)])
  }
  const handleNotUploading = () => {
    setIsUploading(false)
    if (onUploadingStateChange) onUploadingStateChange(false)
  }

  const handleToggleExpanded = () => {
    onUpdate(index, "expanded", !expanded)
  }

  const handleRemove = () => {
    onRemove(index)
  }

  return (
    <ExpansionPanel
      {...{
        title: name,
        description: responseTypeLabels[responseType],
        sortable,
        sortableTitle: `Step ${index + 1}`,
        expanded,
        onChange: () => handleToggleExpanded(),
        placeholders: { title: "Step name", description: "Response type" },
        TransitionProps: { unmountOnExit: true },
      }}
    >
      <Box flexDirection="column" flexGrow={1} pl={3} pr={3}>
        <Fields
          index={index}
          initialState={{ ...{ name, description, responseType } }}
          onUpdate={onUpdate}
          disabled={disabled}
        />
        {uploads && uploads.length > 0 && (
          <Box my={1}>
            <UploadingImagesList
              uploaded={uploads}
              uploading={uploading}
              height={120}
              onRemoveUpload={handleRemoveUpload}
              gridItemProps={{
                xs: 3,
              }}
            />
            <UploadingList
              uploaded={uploads}
              uploading={uploading}
              images={false}
              onRemoveUpload={handleRemoveUpload}
            />
          </Box>
        )}
        <Box mt={1} display="flex">
          <CreatorAtAndUploaders
            // onAtClick={handleAtClick}
            onBeforeUpload={handleBeforeUpload}
            onProgress={handleUploadProgress}
            onItemUploaded={handleItemUploaded}
            onAfterUpload={handleNotUploading}
            onError={handleNotUploading}
            disableUploaders={isUploading || disabled}
            disableAt={disabled}
          />
          <Box ml="auto">
            <RemoveIconButton onConfirmedClick={handleRemove} subject="step" disabled={disabled}>
              <DeleteOutlineIcon />
            </RemoveIconButton>
          </Box>
        </Box>
      </Box>
    </ExpansionPanel>
  )
}

export { ProcessStepCreator }
